<template>
  <div class="gallery-converter relative" :class="conversionTypeTo">
    <header class="text-texts-standard-default uppercase text-40 flexing">
      <div class="header-text w-full flexing relative">{{ convertText }}</div>
    </header>

    <main>
      <gallery-converter-header
        :conversion-type-from="conversionTypeFrom"
        :conversion-type-to="conversionTypeTo"
        :conversion-rate="conversionParameters.conversion_rate"
        :conversion-duration="conversionParameters.conversion_duration"
      />

      <p class="status-text text-32 text-texts-standard-default flexing mx-auto">
        {{ statusText }}
      </p>

      <section class="slider-wrapper flexing">
        <div v-if="process" class="flex justify-evenly items-center w-full h-full flex-col">
          <div class="process-progress flexing text-36 text-texts-standard-default font-bold">
            <p class="flex mr-2">
              <span class="text-texts-standard-important">{{ process.converted_pieces }}</span>
              <span>{{ `/${process.total_converted_pieces}` }}</span>
            </p>
            <app-main-icon :icon-size="48" :icon-name="`puzzle-${conversionTypeFrom}`" />
          </div>

          <div class="speed-up-info flex justify-evenly items-center w-full">
            <app-timer
              class="speed-up-timer"
              :time="process.conversion_finishes_in"
              no-min-width
              @countdown-action="speedUp"
            />

            <app-multi-button1
              v-if="process"
              btn-type="credit"
              :btn-text="$t('gallery.speedUp')"
              :btn-count="process.speedup_cost_next_piece"
              add-class="converter-speed-up-btn"
              :popup-data="{
                title: $t('gallery.speedUp'),
                btnText: $t('gallery.speedUp'),
                btnType: 'confirm',
                btnValue: process.speedup_cost_next_piece,
                btnId: 'gallery-convert-speed-up-id',
                text: $t('gallery.speedUpConfirmMessage'),
              }"
              @action="$debounce(speedUp)"
            />
          </div>
        </div>

        <app-slider
          v-else
          class="convert-pieces-slider"
          :min="1"
          :max="maxAmountICanGet"
          :default-value="1"
          tooltips="top"
          show-buttons
          :format="formatSliderTooltip"
          :disabled="myPieceCount < conversionParameters.conversion_rate"
          @set-slider-value="setExchangeAmount"
        />
      </section>

      <p
        class="conversion-time text-32 text-texts-standard-default flex justify-between items-center mx-auto"
      >
        <span>{{ $t('gallery.conversionTime') }}</span>
        <span class="text-texts-standard-important font-bold">
          {{ `${totalTime}h` }}
        </span>
      </p>

      <gallery-converter-footer
        :is-conversion-in-progress="!!process"
        :speedup-cost="process?.speedup_cost_complete || 0"
        :selected-amount="selectedAmountFrom"
        :conversion-type="conversionTypeFrom"
        :is-conversion-possible="isConversionPossible"
        @start-convert="startConvert(conversionParameters.source_parameter, selectedAmountFrom)"
        @speedup-all="speedUpAll"
      />
    </main>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import type { PropType } from 'vue'
import { mapState, mapActions } from 'pinia'
import AppSlider from '../GlobalComponents/AppSlider.vue'
import type { Converter, ConversionProcess, ConversionParameters } from '@/interfaces/gallery/Converter'
import type { GalleryPieceParam } from '@/interfaces/gallery/Pictures'
import { useGalleryConverterStore } from '@/store/pinia/gallery/converterStore'
import { useResponseTaskStore } from '@/store/pinia/responseTaskStore'
import GalleryConverterHeader from './GalleryConverterHeader.vue'
import GalleryConverterFooter from './GalleryConverterFooter.vue'

interface ComponentData {
  selectedAmountTo: number
}

export default defineComponent({
  name: 'GalleryConverter',

  components: { AppSlider, GalleryConverterHeader, GalleryConverterFooter },

  props: {
    converter: {
      type: Object as PropType<Converter>,
      required: true
    },

    process: {
      type: Object as PropType<ConversionProcess>,
      default: null,
    },
  },

  data(): ComponentData {
    return {
      selectedAmountTo: 1,
    }
  },

  computed: {
    ...mapState(useResponseTaskStore, ['getGalleryParams']),

    conversionParameters(): ConversionParameters {
      return this.converter.conversion_parameters
    },

    conversionTypeFrom(): string {
      return (this.conversionParameters.source_parameter as string).split('_')[2]
    },
    
    conversionTypeTo(): string {
      return (this.conversionParameters.target_parameter as string).split('_')[2]
    },

    myPieceCount(): number {
      const pieces = this.getGalleryParams.find(
        (pieces: GalleryPieceParam): boolean => pieces.parameter_name === this.conversionParameters.source_parameter
      )
      return pieces?.value || 0
    },

    isConversionPossible(): boolean {
      return this.myPieceCount >= this.selectedAmountFrom
    },

    maxAmountICanGet(): number {
      return Math.floor(this.myPieceCount / this.conversionParameters.conversion_rate)
    },

    convertText(): string {
      const text = this.$replacePlaceholder(
        this.$t('gallery.convertType'),
        '{type1}',
        this.$t(`gallery.${this.conversionTypeFrom}Piece`),
      )
      return this.$replacePlaceholder(text, '{type2}', this.$t(`gallery.${this.conversionTypeTo}Piece`))
    },

    statusText(): string {
      return this.process ? this.$t('gallery.conversionInProgress') : this.$t('gallery.piecesDoneOneByOne')
    },

    totalTime(): number {
      return this.selectedAmountTo * (this.conversionParameters.conversion_duration / 60 / 60)
    },

    selectedAmountFrom(): number {
      return this.selectedAmountTo * this.conversionParameters.conversion_rate
    }
  },

  created(): void {
    this.selectedAmountTo = 1
  },

  methods: {
    ...mapActions(useGalleryConverterStore, [
      'startConvert',
      'speedupConvert',
    ]),

    speedUp(): void {
      this.speedupConvert(this.process.id, 'next_piece')
    },

    speedUpAll(): void {
      this.speedupConvert(this.process.id, 'complete')
    },

    setExchangeAmount(value: number): void {
      this.selectedAmountTo = value
    },

    formatSliderTooltip(value: number): string {
      return `<div class="flexing ${this.$isWsm ? 'revert-skew' : ''}">${Math.round(value)}x<div class="icon-puzzle-${this.conversionTypeTo}-48 ml-2"/></div>`
    }
  }
})
</script>

<style lang="scss" scoped>
$types: ('rare', 'epic');
.gallery-converter {
  width: 56.25rem;
  height: 46.75rem;
  border: 0.125rem solid #4c648f;
  background-color: #232a40;
  flex-shrink: 0;
  &:first-child {
    margin-right: 1.875rem;
  }
  @each $type in $types {
    &.#{$type} {
      &::before {
        @include background(
          url('#{$path-gallery}converter/converter-decor-#{$type}.avif?v1'),
          contain
        );
        width: 24.625rem;
        height: 18rem;
        content: '';
        position: absolute;
        left: 0;
        top: 0;
      }
    }
  }
  header {
    width: 55.938rem;
    height: 5rem;
    @include background(url('#{$path-gallery}converter/converter-header.avif'), contain);
    .header-text {
      width: 72%;
      line-height: 2.2rem;
    }
  }
  main {
    width: 56.125rem;
    height: 40.375rem;
    @include background(url('#{$path-gallery}converter/converter-body.avif'), contain);
    background-position: 0 -5rem;
    .status-text {
      width: 48.75rem;
      padding: 1.375rem 0;
      border-bottom: 0.063rem solid rgba($color: #fff, $alpha: 0.3);
    }
    .slider-wrapper {
      height: 13rem;
      width: 48.75rem;
      margin: 0 auto;
      border-bottom: 0.063rem solid rgba($color: #fff, $alpha: 0.3);
      .speed-up-timer {
        width: 16.875rem;
      }
    }
    .convert-pieces-slider {
      width: 40rem;
      top: 2rem;
    }
    .conversion-time {
      width: 52rem;
      padding: 1.375rem 0;
    }
    .process-progress {
      width: 26.313rem;
      height: 4.35rem;
      border-style: solid;
      border-width: 0.125rem;
      border-image-slice: 1;
      @if $isWsm {
        background: linear-gradient(to right, transparent 10%, #6c9ec4 50%, transparent 90%);
        border-image-source: linear-gradient(to right, transparent, #fff, transparent);
      }
      @if $isSsm {
        background: linear-gradient(to right, transparent 10%, #7c7bb2 50%, transparent 90%);
        border-image-source: linear-gradient(to right, transparent, #bac8da, transparent);
      }
    }
  }
}
</style>
